"use client";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { useEffect } from "react";
import Link from "next/link";

import { Input } from "@/components/fields/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useWizardActions, useWizardState } from "@/contexts/wizard-context";

const personalInfoSchema = z.object({
  fullName: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  state: z.string().min(1, "Estado é obrigatório"),
  city: z.string().min(1, "Cidade é obrigatória"),
  email: z.string().email("Email inválido"),
  phone: z.string().min(10, "Telefone deve ter pelo menos 10 dígitos"),
  lgpdConsent: z.literal(true, {
    message: "Você precisa aceitar a Política de Privacidade para continuar.",
  }),
  lgpdConsentAt: z.string().optional(),
});

type PersonalInfoForm = z.infer<typeof personalInfoSchema>;

const PersonalForm = () => {
  const wizardActions = useWizardActions();
  const { formData } = useWizardState();

  const savedData = formData.personal as PersonalInfoForm | undefined;

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isValid },
  } = useForm<PersonalInfoForm>({
    resolver: zodResolver(personalInfoSchema),
    mode: "onChange",
    defaultValues: savedData || {
      fullName: "",
      state: "",
      city: "",
      email: "",
      phone: "",
      lgpdConsent: false as unknown as true,
      lgpdConsentAt: undefined,
    },
  });

  const watchedData = watch();

  useEffect(() => {
    if (isValid && watchedData) {
      wizardActions.updateFormData("personal", {
        ...watchedData,
        lgpdConsentAt: watchedData.lgpdConsentAt ?? new Date().toISOString(),
      });
    }
  }, [isValid]);

  const onSubmit = (data: PersonalInfoForm) => {
    wizardActions.updateFormData("personal", {
      ...data,
      lgpdConsentAt: data.lgpdConsentAt ?? new Date().toISOString(),
    });
    wizardActions.onNextPage();
  };

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <h2 className="text-xl font-medium">Informações Pessoais</h2>
        <p className="text-sm font-light">
          Preencha suas informações básicas. Esses dados são essenciais para que
          as empresas possam entrar em contato com você.
        </p>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full flex flex-col gap-6"
      >
        <Input
          label="Nome completo"
          placeholder="Escreva seu nome completo"
          {...register("fullName")}
          error={errors.fullName?.message}
        />
        <div className="w-full grid grid-cols-2 gap-4 max-sm:grid-cols-1">
          <Input
            label="Estado"
            placeholder="Selecione"
            {...register("state")}
            error={errors.state?.message}
          />
          <Input
            label="Cidade"
            placeholder="Selecione"
            {...register("city")}
            error={errors.city?.message}
          />
        </div>
        <div className="w-full grid grid-cols-2 gap-4 max-sm:grid-cols-1">
          <Input
            label="E-mail"
            placeholder="email@mail.com.br"
            type="email"
            {...register("email")}
            error={errors.email?.message}
          />
          <Input
            label="Telefone"
            placeholder="+55 21 99999-9999"
            type="tel"
            {...register("phone")}
            error={errors.phone?.message}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Controller
            control={control}
            name="lgpdConsent"
            render={({ field }) => (
              <label className="flex gap-2 items-start text-xs font-light cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-primary cursor-pointer"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                <span>
                  Li e concordo com a{" "}
                  <Link
                    href="/privacy-policy"
                    target="_blank"
                    className="text-primary underline"
                  >
                    Política de Privacidade
                  </Link>{" "}
                  e autorizo o uso dos meus dados para gerar meu currículo.
                </span>
              </label>
            )}
          />
          {errors.lgpdConsent && (
            <span className="text-red-500 text-xs">
              {errors.lgpdConsent.message}
            </span>
          )}
        </div>

        <button
          type="submit"
          disabled={!isValid}
          className="btn btn-primary w-fit max-sm:w-full"
        >
          Próximo
        </button>
      </form>
    </div>
  );
};

export { PersonalForm };
